/**
 * "Invite to Teslyn" on the Staff page and linking a register person to their sign-in on acceptance (MB decision
 * 660, Q585; docs/specs/cloud/staff-register.md §3.2; supabase/migrations/20261026001100_invite_links_staff.sql).
 *
 * - A tenant admin presses "Invite to Teslyn" on a person's row: the invitation goes through the invite-user Edge
 *   Function with the person's id, and the person is linked to the sign-in as soon as it is accepted (the first
 *   sign-in). The sign-in roles offered start from the person's register roles (`defaultInviteRoles`).
 * - An invitation from Users & roles is matched on acceptance to the one live, unlinked register person whose email is
 *   the invited address; if several people have it, none is linked and the Staff page asks for a choice by hand.
 * - `staffInviteNote` is what a row of the Staff page says about it.
 * - Decision 867 (Q772): no staff register roles are ever assigned automatically. An invitation from Sign-in and
 *   roles carries the register roles the inviter chose (`inviteRegisterRolesIssue` makes the choice required); a
 *   person added to the register on acceptance has exactly those; a person the register has keeps its roles.
 *
 * The database decides and links; this module only words it.
 */
import type { Person, RegisterRole, Uuid } from './model.js';
import { lifecycleDateText } from './project-lifecycle.js';

/**
 * Teslyn sign-in roles a staff invitation can give, in the order the tick boxes use (invite-user's TENANT_ROLES).
 * Pilot (R156, decision 917) is the phone-only sign-in: on its own it opens Teslyn Mobile's map and photos only.
 */
export const INVITE_STAFF_ROLES = [
  'tenant_admin',
  'manager',
  'field_crew',
  'office_staff',
  'data_processor',
  'pilot',
] as const;
export type InviteStaffRole = (typeof INVITE_STAFF_ROLES)[number];

/**
 * The sign-in role a register role suggests: pilots the Pilot sign-in (R156: phone only), operators field crew,
 * processors data processors; office staff and managers keep their names. Someone who is pilot and operator gets both
 * (Pilot and Field crew), so they keep everything field crew have. Tenant admin is never suggested: it is ticked by
 * hand.
 */
export const REGISTER_ROLE_SIGN_IN: Readonly<Record<RegisterRole, InviteStaffRole>> = {
  pilot: 'pilot',
  operator: 'field_crew',
  office_staff: 'office_staff',
  processor: 'data_processor',
  manager: 'manager',
};

/** The sign-in roles ticked to start with for a person: from their register roles, once each, in role order. */
export function defaultInviteRoles(person: Pick<Person, 'role' | 'roles'>): InviteStaffRole[] {
  const from = new Set((person.roles ?? [person.role]).map((r) => REGISTER_ROLE_SIGN_IN[r]));
  return INVITE_STAFF_ROLES.filter((r) => from.has(r));
}

/**
 * Decision 867: an invitation from Sign-in and roles must say what the person is in the staff register (Pilot,
 * Operator, Office staff, Processor, Manager); null when at least one is chosen, else what to do.
 */
export function inviteRegisterRolesIssue(registerRoles: readonly string[]): string | null {
  return registerRoles.length === 0
    ? "Choose the person's staff register roles: at least one of Pilot, Operator, Office staff, Processor or Manager."
    : null;
}

/** Only tenant admins invite staff (invite-user's rule); platform admins too, where they see the page. */
export function canInviteToTeslyn(roles: readonly string[]): boolean {
  return roles.includes('tenant_admin') || roles.includes('platform_admin');
}

export type StaffInviteOutcome =
  | 'pending'
  | 'linked'
  | 'matched'
  /** N11 (decision 474): linked to the one register person with the sign-in's name (nobody had the email). */
  | 'matched_name'
  /** N11 (decision 474): nobody had the email or the name, so the person was added to the register. */
  | 'created'
  | 'several'
  | 'no_match'
  | 'already_linked'
  | 'not_linked'
  | 'replaced';

/** A row of `staff_invite`, as tenant admins, managers and office staff read it. */
export interface StaffInvite {
  readonly id: Uuid;
  readonly tenant_id: Uuid;
  readonly user_id: Uuid;
  readonly email: string;
  readonly person_id: Uuid | null;
  readonly source: 'staff' | 'users';
  readonly invited_at: string;
  readonly accepted_at: string | null;
  readonly outcome: StaffInviteOutcome;
  readonly linked_person_id: Uuid | null;
  readonly candidates: readonly Uuid[];
  readonly note: string;
}

export interface StaffInviteNote {
  readonly kind: 'invited' | 'linked_on_acceptance' | 'choose' | 'not_linked';
  readonly text: string;
}

const newestFirst = (a: StaffInvite, b: StaffInvite): number =>
  b.invited_at.localeCompare(a.invited_at) || b.id.localeCompare(a.id);

/**
 * What the Staff page says on a person's row about invitations, or null for nothing:
 * - linked by matching a Users & roles invitation: "Linked to <sign-in>'s sign-in on acceptance" (a link from the
 *   Staff page's own invitation shows just the sign-in's name, in the Sign-in column);
 * - not linked, with a pending "Invite to Teslyn": "Invited <date>";
 * - not linked, one of several people an accepted invitation's email matched (and that sign-in still linked to
 *   nobody): ask to choose by hand;
 * - not linked, the Staff page's latest invitation refused on acceptance: why.
 * `signInName` names a sign-in (display name or email); `linkedUsers` are the sign-ins linked to a live person now.
 */
export function staffInviteNote(
  person: Pick<Person, 'id' | 'user_id' | 'deleted_at'>,
  invites: readonly StaffInvite[],
  signInName: (userId: Uuid) => string | null,
  linkedUsers: ReadonlySet<Uuid>,
): StaffInviteNote | null {
  if (person.deleted_at) return null;
  const sorted = [...invites].sort(newestFirst);
  if (person.user_id) {
    const how = sorted.find(
      (i) =>
        i.linked_person_id === person.id &&
        i.user_id === person.user_id &&
        (i.outcome === 'matched' ||
          i.outcome === 'matched_name' ||
          i.outcome === 'created' ||
          i.outcome === 'linked'),
    );
    if (!how || how.outcome === 'linked') return null;
    const name = signInName(how.user_id) ?? how.email;
    const when = lifecycleDateText(how.accepted_at ?? how.invited_at);
    // N11 (decision 474): the person added on acceptance, with the roles chosen when inviting (decision 867)
    if (how.outcome === 'created')
      return {
        kind: 'linked_on_acceptance',
        text: `Added to the staff register when ${name} accepted an invitation (${when}), with the roles chosen in the invitation.`,
      };
    return {
      kind: 'linked_on_acceptance',
      text: `Linked to ${name}'s sign-in on acceptance (${when}${how.outcome === 'matched_name' ? ', same name' : ''}).`,
    };
  }
  const own = sorted.find(
    (i) => i.source === 'staff' && i.person_id === person.id && i.outcome !== 'replaced',
  );
  if (own?.outcome === 'pending') {
    return { kind: 'invited', text: `Invited ${lifecycleDateText(own.invited_at)}` };
  }
  const several = sorted.find(
    (i) => i.outcome === 'several' && i.candidates.includes(person.id) && !linkedUsers.has(i.user_id),
  );
  if (several) {
    return {
      kind: 'choose',
      text:
        `${several.email} accepted an invitation, and ${several.candidates.length} people in the register have ` +
        `this email. Link the right person by hand: Edit, then Teslyn sign-in.`,
    };
  }
  if (own?.outcome === 'not_linked' && own.note) {
    return {
      kind: 'not_linked',
      text: `Invited ${lifecycleDateText(own.invited_at)}, not linked: ${own.note}`,
    };
  }
  return null;
}

/** A pending "Invite to Teslyn" for the person, so the row offers no second one. */
export function hasPendingInvite(personId: Uuid, invites: readonly StaffInvite[]): boolean {
  return invites.some((i) => i.source === 'staff' && i.person_id === personId && i.outcome === 'pending');
}

/** The answer invite-user gives about the link (`link` in its 200 response). */
export interface InviteLinkResult {
  readonly outcome: StaffInviteOutcome | 'not_recorded';
  readonly person_name?: string | null;
  readonly candidates?: readonly Uuid[];
  readonly error?: string;
}

/**
 * The words after an invitation is sent, for the Staff page (`personName` given) or Users & roles. `link` undefined
 * or null: an invite service from before decision 660, which records nothing.
 */
export function inviteLinkMessage(
  email: string,
  status: 'invited' | 'added',
  link: InviteLinkResult | null | undefined,
  personName?: string,
): string {
  const sent =
    status === 'invited'
      ? `Invitation sent to ${email}.`
      : `${email} already had an account and now has the access you chose.`;
  if (!link) {
    return personName
      ? `${sent} Link ${personName} to the sign-in by hand (Edit, then Teslyn sign-in) once they have accepted: ` +
          'the invite service is not updated yet.'
      : sent;
  }
  switch (link.outcome) {
    case 'pending':
      return personName
        ? `${sent} ${personName} is linked to the sign-in as soon as they accept.`
        : `${sent} When it is accepted, the one person in the staff register with this email (or else with their name) is linked to the sign-in; if there is nobody, they are added to the register with the roles you chose.`;
    case 'linked':
      return `${sent} ${link.person_name ?? personName ?? 'The person'} is now linked to the sign-in.`;
    case 'matched':
      return `${sent} Linked to ${link.person_name ?? 'the staff register person'} in the staff register (same email).`;
    case 'matched_name':
      return `${sent} Linked to ${link.person_name ?? 'the staff register person'} in the staff register (same name).`;
    case 'created':
      return `${sent} ${link.person_name ?? 'They'} ${link.person_name ? 'was' : 'were'} added to the staff register with the roles chosen in the invitation.`;
    case 'several':
      return `${sent} ${link.candidates?.length ?? 'Several'} people in the staff register have this email or name: link the right one by hand on the Staff page.`;
    case 'already_linked':
      return `${sent} The sign-in was linked to ${link.person_name ?? 'a person'} in the staff register already.`;
    case 'not_recorded':
      return `${sent} The link to the staff register was not recorded (${link.error ?? 'unknown error'}): link the sign-in by hand on the Staff page.`;
    default:
      return sent;
  }
}
